import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  ChevronRightIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  FunnelIcon,
  ViewColumnsIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const EmployeeDirectory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  const departments = [
    'all',
    'Cardiology',
    'Emergency Medicine',
    'Laboratory Services',
    'Orthopedics',
    'Rehabilitation',
    'Pediatrics'
  ];

  const itemsPerPage = 9;

  useEffect(() => {
    // Sample employee data (expanded from Homepage.js newColleagues)
    const allEmployees = [
      {
        id: 1,
        name: "Dr. Anita Reddy",
        position: "Cardiologist",
        department: "Cardiology",
        joinDate: "July 15, 2025",
        email: "anita.reddy@apollo.com",
        phone: "+91 98765 43210",
        location: "Block A, Floor 3",
        image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
        status: "active",
        specialization: "Interventional Cardiology",
        experience: "12 years"
      },
      {
        id: 2,
        name: "Arjun Singh",
        position: "Nurse Practitioner",
        department: "Emergency Medicine",
        joinDate: "July 18, 2025",
        email: "arjun.singh@apollo.com",
        phone: "+91 98765 43211",
        location: "Emergency Wing",
        image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=150&h=150&fit=crop&crop=face",
        status: "active",
        specialization: "Critical Care",
        experience: "8 years"
      },
      {
        id: 3,
        name: "Deepika Joshi",
        position: "Medical Technologist",
        department: "Laboratory Services",
        joinDate: "July 20, 2025",
        email: "deepika.joshi@apollo.com",
        phone: "+91 98765 43212",
        location: "Laboratory Block",
        image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face&auto=format",
        status: "active",
        specialization: "Clinical Chemistry",
        experience: "5 years"
      },
      {
        id: 4,
        name: "Dr. Rajesh Kumar",
        position: "Orthopedic Surgeon",
        department: "Orthopedics",
        joinDate: "June 1, 2023",
        email: "rajesh.kumar@apollo.com",
        phone: "+91 98765 43213",
        location: "Block B, Floor 2",
        image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=150&h=150&fit=crop&crop=face",
        status: "active",
        specialization: "Joint Replacement",
        experience: "15 years"
      },
      {
        id: 5,
        name: "Priya Sharma",
        position: "Physiotherapist",
        department: "Rehabilitation",
        joinDate: "March 15, 2024",
        email: "priya.sharma@apollo.com",
        phone: "+91 98765 43214",
        location: "Rehabilitation Center",
        image: "https://images.unsplash.com/photo-1594824848402-d2ba4c3e3c65?w=150&h=150&fit=crop&crop=face",
        status: "active",
        specialization: "Sports Physiotherapy",
        experience: "6 years"
      },
      {
        id: 6,
        name: "Dr. Meera Patel",
        position: "Pediatrician",
        department: "Pediatrics",
        joinDate: "January 10, 2024",
        email: "meera.patel@apollo.com",
        phone: "+91 98765 43215",
        location: "Children's Wing",
        image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=150&h=150&fit=crop&crop=face",
        status: "active",
        specialization: "Neonatology",
        experience: "10 years"
      }
    ];

    // Simulate loading
    setTimeout(() => {
      setEmployees(allEmployees);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         employee.position.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         employee.department.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || employee.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleEmployeeClick = (employeeId) => {
    navigate(`/directory/employee/${employeeId}`);
  };

  const EmployeeCard = ({ employee }) => (
    <div 
      className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-apollo-accent/30 transform hover:-translate-y-2 cursor-pointer"
      onClick={() => handleEmployeeClick(employee.id)}
    >
      {/* Header with gradient */}
      <div className="relative h-16 sm:h-20 bg-gradient-to-r from-apollo-accent to-blue-600">
        <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white/20 rounded-full flex items-center justify-center">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Profile Image */}
      <div className="relative -mt-8 sm:-mt-10 flex justify-center">
        <img
          src={employee.image}
          alt={employee.name}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover border-4 border-white shadow-lg group-hover:scale-110 transition-transform duration-300"
        />
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 pt-3 sm:pt-4 text-center">
        <h3 className="text-base sm:text-lg font-bold text-apollo-primary mb-1 group-hover:text-apollo-accent transition-colors">
          {employee.name}
        </h3>
        <p className="text-apollo-accent font-medium text-xs sm:text-sm mb-1">
          {employee.position}
        </p>
        <p className="text-gray-600 text-xs mb-3">
          {employee.department}
        </p>

        {/* Contact Info */}
        <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
          <div className="flex items-center justify-center text-xs text-gray-500">
            <MapPinIcon className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{employee.location}</span>
          </div>
          <div className="flex items-center justify-center text-xs text-gray-500">
            <EnvelopeIcon className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{employee.email}</span>
          </div>
        </div>

        {/* View Profile Button */}
        <button className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-2 px-3 sm:px-4 rounded-xl hover:from-apollo-accent/90 hover:to-blue-600/90 transition-all duration-200 font-medium text-xs sm:text-sm">
          View Profile
        </button>
      </div>
    </div>
  );

  const EmployeeListItem = ({ employee }) => (
    <div 
      className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-4 sm:p-6 border border-gray-100 hover:border-apollo-accent/30 cursor-pointer"
      onClick={() => handleEmployeeClick(employee.id)}
    >
      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
        <img
          src={employee.image}
          alt={employee.name}
          className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-cover mx-auto sm:mx-0 flex-shrink-0"
        />
        <div className="flex-1 text-center sm:text-left min-w-0">
          <h3 className="text-base sm:text-lg font-bold text-apollo-primary mb-1">
            {employee.name}
          </h3>
          <p className="text-apollo-accent font-medium mb-1 text-sm sm:text-base">
            {employee.position}
          </p>
          <p className="text-gray-600 text-sm">
            {employee.department}
          </p>
        </div>
        <div className="text-center sm:text-right space-y-1">
          <div className="flex items-center justify-center sm:justify-end text-xs sm:text-sm text-gray-500 mb-1">
            <MapPinIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
            <span className="truncate">{employee.location}</span>
          </div>
          <div className="flex items-center justify-center sm:justify-end text-xs sm:text-sm text-gray-500 mb-1">
            <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
            <span className="truncate">{employee.phone}</span>
          </div>
          <div className="flex items-center justify-center sm:justify-end text-xs sm:text-sm text-gray-500">
            <EnvelopeIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
            <span className="truncate">{employee.email}</span>
          </div>
        </div>
        <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mx-auto sm:mx-0 flex-shrink-0" />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading employee directory...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      {/* <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <UserGroupIcon className="w-16 h-16 mx-auto mb-4 opacity-80" />
            <h1 className="text-4xl font-bold mb-4">Employee Directory</h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Connect with your colleagues across Apollo Healthcare
            </p>
          </div>
        </div>
      </div> */}

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:gap-4 lg:items-center lg:justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-full lg:max-w-lg">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees by name, position, or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 text-sm sm:text-base"
              />
            </div>

            {/* Department Filter and View Mode */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full sm:w-auto pl-8 sm:pl-10 pr-8 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white text-sm sm:text-base"
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>
                      {dept === 'all' ? 'All Departments' : dept}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    viewMode === 'grid'
                      ? 'bg-apollo-accent text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Squares2X2Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    viewMode === 'list'
                      ? 'bg-apollo-accent text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <ViewColumnsIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 sm:mb-6">
          <p className="text-gray-600 text-sm sm:text-base">
            Showing {paginatedEmployees.length} of {filteredEmployees.length} employees
          </p>
        </div>

        {/* Employee Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {paginatedEmployees.map(employee => (
              <EmployeeCard key={employee.id} employee={employee} />
            ))}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
            {paginatedEmployees.map(employee => (
              <EmployeeListItem key={employee.id} employee={employee} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center">
            <div className="flex space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-apollo-accent text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default EmployeeDirectory;
